import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  setUpTOTP,
  verifyTOTPSetup,
  updateMFAPreference,
  fetchMFAPreference,
  getCurrentUser,
} from 'aws-amplify/auth';
import { QRCodeSVG } from 'qrcode.react';
import { FaTimes, FaShieldAlt, FaCheckCircle, FaCopy, FaExclamationTriangle } from 'react-icons/fa';
import { useTranslation } from '../../i18n';
import './MFASetup.css';

const APP_NAME = 'DataBunker';

const MFASetup = ({ onClose }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState('loading');
  const [totpUri, setTotpUri] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);
  const setupInFlight = useRef(false);
  const initialized = useRef(false);

  const startSetup = useCallback(async () => {
    if (setupInFlight.current) return;
    setupInFlight.current = true;
    setStep('loading');
    setError('');
    try {
      const user = await getCurrentUser();
      const totpDetails = await setUpTOTP();
      const accountName = user.signInDetails?.loginId || user.username;
      const setupUri = totpDetails.getSetupUri(APP_NAME, accountName);
      setTotpUri(setupUri.toString());
      setSecret(totpDetails.sharedSecret);
      setStep('setup');
    } catch (e) {
      console.error('[MFASetup] startSetup error:', e);
      setError(e.message || t('mfa.errorGeneric'));
      setStep('error');
    } finally {
      setupInFlight.current = false;
    }
  }, [t]);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    const checkExistingMFA = async () => {
      try {
        const pref = await fetchMFAPreference();
        if (pref?.enabled?.includes('TOTP')) {
          setStep('alreadyEnabled');
        } else {
          startSetup();
        }
      } catch (e) {
        console.error('[MFASetup] fetchMFAPreference error:', e);
        startSetup();
      }
    };
    checkExistingMFA();
  }, [startSetup]);

  const handleVerify = async () => {
    if (code.length !== 6) return;
    setLoading(true);
    setError('');
    try {
      await verifyTOTPSetup({ code });
      await updateMFAPreference({ totp: 'PREFERRED' });
      setStep('success');
    } catch (e) {
      console.error('[MFASetup] verify error:', e);
      setError(e.message || t('mfa.errorInvalidCode'));
    } finally {
      setLoading(false);
    }
  };

  const handleReconfigure = async () => {
    setLoading(true);
    setError('');
    try {
      await updateMFAPreference({ totp: 'DISABLED' });
      startSetup();
    } catch (e) {
      console.error('[MFASetup] reconfigure error:', e);
      setError(e.message || t('mfa.errorGeneric'));
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    setLoading(true);
    setError('');
    try {
      await updateMFAPreference({ totp: 'DISABLED' });
      setStep('removed');
    } catch (e) {
      console.error('[MFASetup] remove error:', e);
      setError(e.message || t('mfa.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    } catch (e) {
      console.error('[MFASetup] copy error:', e);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
  };

  const handleCodeKeyDown = (e) => {
    if (e.key === 'Enter' && code.length === 6 && !loading) {
      handleVerify();
    }
  };

  const modalContent = (
    <div className="mfa-overlay" onClick={onClose}>
      <div className="mfa-modal" onClick={(e) => e.stopPropagation()}>
        <button className="mfa-close-btn" onClick={onClose} aria-label="Close">
          <FaTimes />
        </button>

        <div className="mfa-header">
          <FaShieldAlt className="mfa-header-icon" />
          <h2>{t('mfa.title')}</h2>
        </div>

        {step === 'loading' && (
          <div className="mfa-loading">
            <div className="mfa-spinner" />
            <p>{t('mfa.loading')}</p>
          </div>
        )}

        {step === 'alreadyEnabled' && (
          <div className="mfa-already-enabled">
            <FaCheckCircle className="mfa-check-icon" />
            <p className="mfa-status-text">{t('mfa.alreadyEnabled')}</p>
            <p className="mfa-status-subtext">{t('mfa.alreadyEnabledDesc')}</p>
            {error && <p className="mfa-error-msg">{error}</p>}
            <div className="mfa-actions mfa-actions-wrap">
              <button className="mfa-btn-secondary" onClick={onClose}>
                {t('mfa.close')}
              </button>
              <button
                className="mfa-btn-danger"
                onClick={() => {
                  setError('');
                  setStep('confirmRemove');
                }}
                disabled={loading}
              >
                {t('mfa.remove')}
              </button>
              <button
                className="mfa-btn-primary"
                onClick={handleReconfigure}
                disabled={loading}
              >
                {loading ? t('mfa.processing') : t('mfa.reconfigure')}
              </button>
            </div>
          </div>
        )}

        {step === 'confirmRemove' && (
          <div className="mfa-confirm-remove">
            <FaExclamationTriangle className="mfa-warning-icon" />
            <h3>{t('mfa.removeConfirmTitle')}</h3>
            <p className="mfa-warning-text">{t('mfa.removeConfirmDesc')}</p>
            {error && <p className="mfa-error-msg">{error}</p>}
            <div className="mfa-actions">
              <button
                className="mfa-btn-secondary"
                onClick={() => {
                  setError('');
                  setStep('alreadyEnabled');
                }}
                disabled={loading}
              >
                {t('mfa.cancel')}
              </button>
              <button
                className="mfa-btn-danger"
                onClick={handleRemove}
                disabled={loading}
              >
                {loading ? t('mfa.processing') : t('mfa.confirmRemove')}
              </button>
            </div>
          </div>
        )}

        {step === 'removed' && (
          <div className="mfa-success-step">
            <FaCheckCircle className="mfa-success-icon mfa-icon-warning" />
            <h3>{t('mfa.removedTitle')}</h3>
            <p>{t('mfa.removedDesc')}</p>
            <div className="mfa-actions mfa-actions-center">
              <button className="mfa-btn-primary" onClick={onClose}>
                {t('mfa.close')}
              </button>
            </div>
          </div>
        )}

        {step === 'setup' && (
          <div className="mfa-setup-step">
            <p className="mfa-instructions">{t('mfa.scanInstructions')}</p>

            <div className="mfa-qr-container">
              <QRCodeSVG value={totpUri} size={180} level="M" includeMargin />
            </div>

            <details className="mfa-manual">
              <summary>{t('mfa.cantScan')}</summary>
              <p className="mfa-manual-desc">{t('mfa.manualSecret')}</p>
              <div className="mfa-secret-box">
                <code className="mfa-secret-code">{secret}</code>
                <button
                  className="mfa-copy-btn"
                  onClick={copySecret}
                  aria-label="Copy"
                >
                  <FaCopy />
                  <span>{secretCopied ? t('mfa.copied') : t('mfa.copy')}</span>
                </button>
              </div>
            </details>

            <div className="mfa-actions">
              <button className="mfa-btn-secondary" onClick={onClose}>
                {t('mfa.cancel')}
              </button>
              <button
                className="mfa-btn-primary"
                onClick={() => setStep('verify')}
              >
                {t('mfa.next')}
              </button>
            </div>
          </div>
        )}

        {step === 'verify' && (
          <div className="mfa-verify-step">
            <p className="mfa-instructions">{t('mfa.enterCode')}</p>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              className="mfa-code-input"
              value={code}
              onChange={handleCodeChange}
              onKeyDown={handleCodeKeyDown}
              placeholder="000000"
              autoFocus
              maxLength="6"
            />
            {error && <p className="mfa-error-msg">{error}</p>}
            <div className="mfa-actions">
              <button
                className="mfa-btn-secondary"
                onClick={() => {
                  setError('');
                  setCode('');
                  setStep('setup');
                }}
              >
                {t('mfa.back')}
              </button>
              <button
                className="mfa-btn-primary"
                onClick={handleVerify}
                disabled={code.length !== 6 || loading}
              >
                {loading ? t('mfa.verifying') : t('mfa.verify')}
              </button>
            </div>
          </div>
        )}

        {step === 'success' && (
          <div className="mfa-success-step">
            <FaCheckCircle className="mfa-success-icon" />
            <h3>{t('mfa.successTitle')}</h3>
            <p>{t('mfa.successDesc')}</p>
            <div className="mfa-actions mfa-actions-center">
              <button className="mfa-btn-primary" onClick={onClose}>
                {t('mfa.close')}
              </button>
            </div>
          </div>
        )}

        {step === 'error' && (
          <div className="mfa-error-state">
            <p className="mfa-error-msg">{error}</p>
            <div className="mfa-actions">
              <button className="mfa-btn-secondary" onClick={onClose}>
                {t('mfa.close')}
              </button>
              <button className="mfa-btn-primary" onClick={startSetup}>
                {t('mfa.retry')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default MFASetup;
